import { ChangeEvent, useState } from 'react'
import { useController } from 'react-hook-form'
import { FormControl, FormGroup, FormHelperText, FormLabel } from '@mui/material'
import { clone } from 'lodash'

import { DefaultElementProps } from 'common/interfaces'
import {
  CheckboxGroup as CheckboxGroupProps,
  CheckboxGroupOption,
} from 'elements/CheckboxGroup/interface'
import { CheckboxImageOption } from 'elements/CheckboxGroupImage/interface'
import { validationToCheckboxRules } from 'utils/validationsToRules'

import { ImageLogicGroupProps } from '../RadioGroupWrapper/RadioGroupWrapper'
import { SizingContainer } from '../SizingContainer'

type CheckboxGroupWrapperProps = Omit<CheckboxGroupProps, 'options'> &
  DefaultElementProps &
  ImageLogicGroupProps & {
    renderOption: (
      option: CheckboxGroupOption | CheckboxImageOption,
      valueState: Record<string, boolean>,
      handleChange: (id: string, event: ChangeEvent<HTMLInputElement>) => void
    ) => JSX.Element
    options: CheckboxGroupOption[] | CheckboxImageOption[]
    defaultValue: Record<any, any>
  }

export default function CheckboxGroupWrapper({
  name,
  size,
  label,
  options,
  direction,
  validations,
  control,
  renderOption,
  defaultValue,
  onChange,
  onFocus,
  gapSize,
  textAlign = 'left',
  classNames,
}: CheckboxGroupWrapperProps): JSX.Element {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control: control,
    defaultValue,
    rules: validationToCheckboxRules(validations),
  })

  const [value, setValue] = useState<Record<string, boolean>>(field.value)

  function handleChange(id: string, event: ChangeEvent<HTMLInputElement>): void {
    const newValue = clone(value)
    newValue[id] = event.target.checked

    field.onChange(newValue)
    onChange?.({ value: newValue })
    setValue(newValue)
  }

  return (
    <SizingContainer className={classNames} size={size}>
      <FormControl error={error !== undefined} sx={{ width: 1 }} onFocus={onFocus}>
        {label !== undefined && (
          <FormLabel required={validations?.required?.value}>{label}</FormLabel>
        )}
        <FormGroup
          row={direction === 'row'}
          sx={{
            overflow: 'hidden',
            gap: gapSize,
            '& .image-card-txt-wrap': {
              textAlign: textAlign,
            },
          }}
        >
          {options.map((option) => renderOption(option, value, handleChange))}
        </FormGroup>
        {error !== undefined && <FormHelperText>{error.message}</FormHelperText>}
      </FormControl>
    </SizingContainer>
  )
}
